import React, { useCallback } from 'react';
import { Dropdown } from 'semantic-ui-react';
import styled from 'styled-components';
import {
  COURSE_TYPE,
  ACCESS_TYPE,
  STUDENT_COURSE_STATES,
} from 'constants/student-courses';
import EditIcon from 'img/edit.svg';
import { DropdownItem, DropdownMenu, DropdownText } from 'comps/dropdown';
import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';
import { MODAL_FORM, MODAL_CONFIRMATION_ACTIONS } from 'constants/form';
import {
  useActivateCoursePatch,
  useSuspendCoursePatch,
  invalidateStudentCoursesGetAll,
} from 'actions/studentsv2';

import { getCookie } from 'utils';

const TableCellRelative = styled.div`
  position: relative;
`;

const ImageButton = styled.img`
  cursor: pointer;
`;

function CourseActions({ course, actions, studentId, grade, courseListData }) {
  const {
    id,
    access_type: accessType,
    status,
    cue_course_type: cueCourseType,
    downgrade_allowed: downgradeAllowed = false,
    state,
  } = course;

  const userId = getCookie('user_id') || '';
  const { patch: suspendCoursePatch } = useSuspendCoursePatch({
    onComplete: () => invalidateStudentCoursesGetAll(),
  });
  const { patch: activateCoursePatch } = useActivateCoursePatch({
    onComplete: () => invalidateStudentCoursesGetAll(),
  });

  const openStudentUpgradeV2Form = studentCourse => {
    trackEvent(EVENT_TYPE.STUDENT_UPGRADE_V2_CLICK, {
      course: studentCourse,
      userId,
    });

    actions.openModal(
      'studentUpgradeV2Modal',
      {
        size: 'custom',
        width: `650px`,
        height: '560px',
      },
      {
        studentCourse,
      },
    );
  };

  const openStudentPauseUnitsForm = studentCourse => {
    actions.openModal(
      MODAL_FORM.PAUSE_UNITS_MODAL,
      {
        size: 'custom',
        width: `650px`,
      },
      {
        studentId,
        studentCourseId: studentCourse.id,
        endTime: studentCourse.validity_till_date,
      },
    );
  };

  const openTransferClassesForm = (studentCourse, isPartialTransfer) => {
    trackEvent(EVENT_TYPE.TRANSFER_CLASS_CLICK, {
      course: studentCourse,
      userId,
    });
    actions.openModal(
      'studentTransferClassesModal',
      {
        size: 'custom',
        width: `600px`,
        height: '540px',
      },
      {
        studentCourse,
        grade,
        isPartialTransfer,
        studentId,
        courseListData: isPartialTransfer ? courseListData : null,
      },
    );
  };

  const openConvertClassesToCreditForm = studentCourse => {
    trackEvent(EVENT_TYPE.CONVERT_CLASSES_TO_CREDIT_CLICK, {
      course: studentCourse,
      userId,
    });
    actions.openModal(
      'studentConvertClassesToCreditModal',
      {
        size: 'custom',
        width: '600px',
        height: 'fit-content',
        maxHeight: '80vh',
      },
      {
        studentCourse,
        studentId,
      },
    );
  };

  const openHybridDowngradeForm = studentCourse => {
    trackEvent(EVENT_TYPE.COURSE_DOWNGRADE_CLICK, {
      course: studentCourse,
      userId,
    });
    actions.openModal(
      'studentCourseDowngradeModal',
      {
        size: 'custom',
        width: '600px',
        height: '540px',
      },
      {
        studentCourse,
      },
    );
  };

  const openEditStudentCourseForm = studentCourse => {
    trackEvent(EVENT_TYPE.MORE_ACTIONS_COURSES, {
      course: studentCourse,
      userId,
    });

    actions.openModal(
      'studentCourseEditModal',
      {
        size: 'custom',
        width: `${Math.ceil((window.innerWidth * 50) / 100)}px`,
        height: 'fit-content',
      },
      {
        studentCourse,
      },
    );
  };

  const showSuspendAccountCTA = [
    STUDENT_COURSE_STATES.ACTIVE,
    STUDENT_COURSE_STATES.PAUSED,
    STUDENT_COURSE_STATES.READMIT,
  ].includes(state);
  const showUnSuspendAccountCTA = state === STUDENT_COURSE_STATES.SUSPENDED;

  const handleSuspendStudentCourse = useCallback(value => {
    if (value === MODAL_CONFIRMATION_ACTIONS.CONFIRMED) {
      suspendCoursePatch(id);
    }
  }, []);

  const handleActivateStudentCourse = useCallback(value => {
    if (value === MODAL_CONFIRMATION_ACTIONS.CONFIRMED) {
      const body = {
        student_course_ids: [id],
      };
      activateCoursePatch(id, body);
    }
  }, []);

  const handleChangeStudentCourseState = useCallback(() => {
    actions.openModal(
      MODAL_FORM.CONFIRMATION,
      {
        blocking: true,
        transparent: false,
      },
      {
        onConfirm: showSuspendAccountCTA
          ? handleSuspendStudentCourse
          : handleActivateStudentCourse,
        title: showSuspendAccountCTA
          ? `Are you sure you want to Suspend the course?`
          : `Are you sure you want to activate the course?`,
        description: '(This may take upto 5 minutes)',
      },
    );
  }, []);

  const shouldShowActions =
    ACCESS_TYPE.WITH_TEACHER === accessType ||
    (status !== 'EXP' && cueCourseType !== COURSE_TYPE.ELECTIVES);

  return (
    <TableCellRelative>
      {shouldShowActions && (
        <Dropdown icon={<ImageButton src={EditIcon} />} className="icon">
          <DropdownMenu left="0px" top="24px !important">
            {ACCESS_TYPE.WITH_TEACHER === accessType && (
              <>
                <DropdownItem onClick={() => openStudentUpgradeV2Form(course)}>
                  <DropdownText variant="body1Regular">Upgrade-v2</DropdownText>
                </DropdownItem>
                <DropdownItem onClick={() => openTransferClassesForm(course, true)}>
                  <DropdownText variant="body1Regular">
                    Transfer Partial classes
                  </DropdownText>
                </DropdownItem>
                <DropdownItem onClick={() => openConvertClassesToCreditForm(course)}>
                  <DropdownText variant="body1Regular">
                    Convert Classes to Credit
                  </DropdownText>
                </DropdownItem>
                <DropdownItem onClick={() => openStudentPauseUnitsForm(course)}>
                  <DropdownText variant="body1Regular">Add pause units</DropdownText>
                </DropdownItem>
                {downgradeAllowed && (
                  <DropdownItem onClick={() => openHybridDowngradeForm(course)}>
                    <DropdownText variant="body1Regular">Downgrade</DropdownText>
                  </DropdownItem>
                )}
                {showSuspendAccountCTA || showUnSuspendAccountCTA ? (
                  <DropdownItem onClick={handleChangeStudentCourseState}>
                    <DropdownText variant="body1Regular">
                      {showSuspendAccountCTA ? 'Suspend course' : 'Unsuspend course'}
                    </DropdownText>
                  </DropdownItem>
                ) : null}
              </>
            )}
            {status !== 'EXP' && cueCourseType !== COURSE_TYPE.ELECTIVES && (
              <DropdownItem onClick={() => openEditStudentCourseForm(course)}>
                <DropdownText variant="body1Regular">More actions</DropdownText>
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      )}
    </TableCellRelative>
  );
}

export default CourseActions;
